import { parse } from 'mathjs';

import { ALL_CONSTANTS, ALL_FUNCTIONS } from './formula-editor-config';

/**
 * Converts a formula string into LaTeX notation for MathJax rendering.
 *
 * **Primary strategy**: Uses mathjs built-in `node.toTex()` which handles
 * all standard mathjs syntax correctly.
 * @see https://mathjs.org/examples/browser/pretty_printing_with_mathjax.html
 *
 * **Fallback**: A custom tokenizer for cases where mathjs parsing fails
 * (e.g. formulas with hyphenated variable names that are not pre-processed).
 *
 * Hyphenated variable names are temporarily replaced with underscored versions
 * for parsing, then restored as `\text{...}` in the LaTeX output.
 */
export function formulaToLatex(formula: string, variableNames: string[] = []): string {
  const trimmed = formula?.trim();
  if (!trimmed) {
    return '';
  }

  // Try primary strategy: mathjs node.toTex()
  try {
    return mathjsToLatex(trimmed, variableNames);
  } catch {
    // Fallback to custom tokenizer
    return fallbackTokenizerToLatex(trimmed);
  }
}

// ---------------------------------------------------------------------------
// Primary: mathjs node.toTex()
// ---------------------------------------------------------------------------

/** Map of hyphenated names → safe underscore names for mathjs parsing */
interface NameMapping {
  hyphenToSafe: Map<string, string>;
  safeToHyphen: Map<string, string>;
}

/**
 * Builds bidirectional name mappings for hyphenated variable names.
 */
function buildNameMapping(variableNames: string[]): NameMapping {
  const hyphenToSafe = new Map<string, string>();
  const safeToHyphen = new Map<string, string>();

  for (const name of variableNames) {
    if (name.includes('-')) {
      const safeName = name.replace(/-/g, '_');
      hyphenToSafe.set(name, safeName);
      safeToHyphen.set(safeName, name);
    }
  }

  return { hyphenToSafe, safeToHyphen };
}

/**
 * Replaces hyphenated variable names with safe underscore versions in the formula.
 * Sorts by length descending to avoid partial replacements.
 */
function replaceHyphensForParsing(formula: string, mapping: NameMapping): string {
  let safe = formula;
  const sorted = [...mapping.hyphenToSafe.entries()].sort(([a], [b]) => b.length - a.length);
  for (const [hyphenated, safeName] of sorted) {
    safe = safe.split(hyphenated).join(safeName);
  }
  return safe;
}

/**
 * Restores hyphenated variable names in the LaTeX output.
 * Replaces `safeName` occurrences back to `\text{originalName}`.
 */
function restoreHyphensInLatex(latex: string, mapping: NameMapping): string {
  let result = latex;
  const sorted = [...mapping.safeToHyphen.entries()].sort(([a], [b]) => b.length - a.length);
  for (const [safeName, originalName] of sorted) {
    // Replace bare safe names and mathrm-wrapped versions
    result = result.split(`\\mathrm{${safeName}}`).join(`\\text{${originalName}}`);
    result = result.split(safeName).join(`\\text{${originalName}}`);
  }
  return result;
}

/**
 * Wraps remaining bare variable names (not functions/constants) in `\text{…}`.
 */
function wrapVariableNamesInLatex(latex: string, variableNames: string[]): string {
  let result = latex;
  const allFunctionsSet = new Set(ALL_FUNCTIONS);
  const allConstantsSet = new Set(ALL_CONSTANTS);

  // Sort by length descending to avoid partial replacements
  const sorted = [...variableNames].sort((a, b) => b.length - a.length);
  for (const name of sorted) {
    if (allFunctionsSet.has(name) || allConstantsSet.has(name)) {
      continue;
    }
    // Only wrap if not already inside a \text{} or \mathrm{}
    const safeName = name.replace(/-/g, '_');
    const escaped = safeName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(?<!\\\\text\\{|\\\\mathrm\\{)\\b${escaped}\\b`, 'g');
    result = result.replace(regex, `\\text{${name}}`);
  }
  return result;
}

/**
 * Uses mathjs `node.toTex()` for accurate LaTeX conversion.
 */
function mathjsToLatex(formula: string, variableNames: string[]): string {
  const mapping = buildNameMapping(variableNames);
  const safeFormula = replaceHyphensForParsing(formula, mapping);

  // Parse formula directly — 'and'/'or' keywords are used; '&&'/'||' are not present.
  // const node = parse(normalizeFormulaForMathJs(safeFormula));
  const node = parse(safeFormula);
  let latex = node.toTex();

  // Restore hyphenated names
  if (mapping.safeToHyphen.size > 0) {
    latex = restoreHyphensInLatex(latex, mapping);
  }

  // Wrap remaining variable names in \text{}
  if (variableNames.length > 0) {
    latex = wrapVariableNamesInLatex(latex, variableNames);
  }

  return latex;
}

// ---------------------------------------------------------------------------
// Fallback: Custom tokenizer (kept for robustness)
// ---------------------------------------------------------------------------

interface NumberToken {
  type: 'number';
  value: string;
}

interface VariableToken {
  type: 'variable';
  value: string;
}

interface FunctionToken {
  type: 'function';
  value: string;
}

interface ConstantToken {
  type: 'constant';
  value: string;
}

interface OperatorToken {
  type: 'operator';
  value: string;
}

interface ParenToken {
  type: 'paren';
  value: '(' | ')';
}

interface CommaToken {
  type: 'comma';
  value: ',';
}

type Token = NumberToken | VariableToken | FunctionToken | ConstantToken | OperatorToken | ParenToken | CommaToken;

/** Set of known math function names */
const KNOWN_FUNCTIONS = new Set(ALL_FUNCTIONS);

/** Set of known constant names */
const KNOWN_CONSTANTS = new Set(ALL_CONSTANTS);

/** Logical / keyword operators written as words */
const WORD_OPERATORS = new Set(['and', 'or', 'xor', 'not', 'to', 'in']);

/** LaTeX commands for math functions that have dedicated LaTeX commands */
const LATEX_FUNCTION_MAP: Record<string, string> = {
  sqrt: '\\sqrt',
  sin: '\\sin',
  cos: '\\cos',
  tan: '\\tan',
  asin: '\\arcsin',
  acos: '\\arccos',
  atan: '\\arctan',
  sinh: '\\sinh',
  cosh: '\\cosh',
  tanh: '\\tanh',
  cot: '\\cot',
  coth: '\\coth',
  csc: '\\csc',
  sec: '\\sec',
  log: '\\log',
  log2: '\\log_2',
  log10: '\\log_{10}',
  exp: '\\exp',
  abs: '\\left|',
  min: '\\min',
  max: '\\max',
  ceil: '\\lceil',
  floor: '\\lfloor',
  gcd: '\\gcd',
  det: '\\det',
  mod: '\\bmod'
};

/** LaTeX for constants */
const LATEX_CONSTANT_MAP: Record<string, string> = {
  pi: '\\pi',
  PI: '\\pi',
  e: 'e',
  E: 'e',
  i: 'i',
  phi: '\\varphi',
  tau: '\\tau',
  Infinity: '\\infty',
  NaN: '\\text{NaN}',
  true: '\\text{true}',
  false: '\\text{false}',
  null: '\\text{null}',
  undefined: '\\text{undefined}',
  LN2: '\\ln 2',
  LN10: '\\ln 10',
  LOG2E: '\\log_2 e',
  LOG10E: '\\log_{10} e',
  SQRT1_2: '\\frac{1}{\\sqrt{2}}',
  SQRT2: '\\sqrt{2}'
};

/** LaTeX for comparison/multi-char operators */
const LATEX_OPERATOR_MAP: Record<string, string> = {
  '>=': ' \\geq ',
  '<=': ' \\leq ',
  '==': ' = ',
  '!=': ' \\neq ',
  '%': ' \\bmod ',
  '&': ' \\mathbin{\\&} ',
  '|': ' \\mid ',
  '~': '\\sim ',
  '<<': ' \\ll ',
  '>>': ' \\gg ',
  '>>>': ' \\ggg ',
  and: ' \\land ',
  or: ' \\lor ',
  xor: ' \\oplus ',
  not: '\\lnot '
};

function tokenize(formula: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < formula.length) {
    const ch = formula[i];

    // Whitespace — skip
    if (/\s/.test(ch)) {
      i++;
      continue;
    }

    // Numbers (integers, decimals, scientific notation)
    if (/\d/.test(ch)) {
      let num = '';
      while (i < formula.length && /[\d.]/.test(formula[i])) {
        num += formula[i];
        i++;
      }
      // Scientific notation
      if (i < formula.length && /[eE]/.test(formula[i])) {
        num += formula[i];
        i++;
        if (i < formula.length && /[+-]/.test(formula[i])) {
          num += formula[i];
          i++;
        }
        while (i < formula.length && /\d/.test(formula[i])) {
          num += formula[i];
          i++;
        }
      }
      tokens.push({ type: 'number', value: num });
      continue;
    }

    // Parentheses
    if (ch === '(' || ch === ')') {
      tokens.push({ type: 'paren', value: ch });
      i++;
      continue;
    }

    // Comma
    if (ch === ',') {
      tokens.push({ type: 'comma', value: ',' });
      i++;
      continue;
    }

    // Triple-character operators (>>>)
    if (i + 2 < formula.length && formula[i] + formula[i + 1] + formula[i + 2] === '>>>') {
      tokens.push({ type: 'operator', value: '>>>' });
      i += 3;
      continue;
    }

    // Multi-character operators (>=, <=, ==, !=, <<, >>)
    if (i + 1 < formula.length) {
      const twoChar = formula[i] + formula[i + 1];
      if (['>=', '<=', '==', '!=', '<<', '>>'].includes(twoChar)) {
        tokens.push({ type: 'operator', value: twoChar });
        i += 2;
        continue;
      }
    }

    // Single-character operators
    if (/[+\-*/^%><&|~?:=]/.test(ch)) {
      tokens.push({ type: 'operator', value: ch });
      i++;
      continue;
    }

    // Variable names, function names, constants, or word operators
    if (/[a-zA-Z_]/.test(ch)) {
      let name = '';
      while (i < formula.length && /[a-zA-Z0-9_-]/.test(formula[i])) {
        name += formula[i];
        i++;
      }
      if (KNOWN_FUNCTIONS.has(name)) {
        tokens.push({ type: 'function', value: name });
      } else if (KNOWN_CONSTANTS.has(name)) {
        tokens.push({ type: 'constant', value: name });
      } else if (WORD_OPERATORS.has(name)) {
        tokens.push({ type: 'operator', value: name });
      } else {
        tokens.push({ type: 'variable', value: name });
      }
      continue;
    }

    // Unknown character — skip
    i++;
  }

  return tokens;
}

function escapeLatexVariable(name: string): string {
  return `\\text{${name}}`;
}

function tokensToLatex(tokens: Token[]): string {
  const parts: string[] = [];

  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];

    switch (token.type) {
      case 'number':
        parts.push(token.value);
        break;

      case 'variable':
        parts.push(escapeLatexVariable(token.value));
        break;

      case 'constant':
        parts.push(LATEX_CONSTANT_MAP[token.value] ?? `\\text{${token.value}}`);
        break;

      case 'function': {
        const fnName = token.value;

        // Expect `(` after function name
        const nextIdx = i + 1;
        if (nextIdx < tokens.length && tokens[nextIdx].type === 'paren' && tokens[nextIdx].value === '(') {
          const argsResult = extractFunctionArgs(tokens, nextIdx);
          i = argsResult.endIndex;

          if (fnName === 'sqrt') {
            parts.push(`\\sqrt{${argsResult.latex}}`);
          } else if (fnName === 'abs') {
            parts.push(`\\left|${argsResult.latex}\\right|`);
          } else if (fnName === 'ceil') {
            parts.push(`\\lceil ${argsResult.latex} \\rceil`);
          } else if (fnName === 'floor') {
            parts.push(`\\lfloor ${argsResult.latex} \\rfloor`);
          } else if (fnName === 'pow' && argsResult.argParts.length === 2) {
            parts.push(`{${argsResult.argParts[0]}}^{${argsResult.argParts[1]}}`);
          } else if (fnName === 'nthRoot' && argsResult.argParts.length === 2) {
            parts.push(`\\sqrt[${argsResult.argParts[1]}]{${argsResult.argParts[0]}}`);
          } else if (fnName === 'factorial' && argsResult.argParts.length === 1) {
            parts.push(`${argsResult.argParts[0]}!`);
          } else if (fnName === 'log2' && argsResult.argParts.length === 1) {
            parts.push(`\\log_2\\left(${argsResult.latex}\\right)`);
          } else if (fnName === 'log10' && argsResult.argParts.length === 1) {
            parts.push(`\\log_{10}\\left(${argsResult.latex}\\right)`);
          } else if (LATEX_FUNCTION_MAP[fnName]) {
            parts.push(`${LATEX_FUNCTION_MAP[fnName]}\\left(${argsResult.latex}\\right)`);
          } else {
            parts.push(`\\operatorname{${fnName}}\\left(${argsResult.latex}\\right)`);
          }
        } else {
          parts.push(`\\operatorname{${fnName}}`);
        }
        break;
      }

      case 'comma':
        parts.push(', ');
        break;

      case 'paren':
        if (token.value === '(') {
          parts.push('\\left(');
        } else {
          parts.push('\\right)');
        }
        break;

      case 'operator':
        if (token.value === '*') {
          parts.push(' \\cdot ');
        } else if (token.value === '/') {
          const numerator = parts.pop() ?? '';
          const denominator = getNextOperand(tokens, i + 1);
          parts.push(`\\frac{${numerator}}{${denominator.latex}}`);
          i = denominator.endIndex;
        } else if (token.value === '^') {
          const exponent = getNextOperand(tokens, i + 1);
          parts.push(`^{${exponent.latex}}`);
          i = exponent.endIndex;
        } else if (LATEX_OPERATOR_MAP[token.value]) {
          parts.push(LATEX_OPERATOR_MAP[token.value]);
        } else {
          parts.push(` ${token.value} `);
        }
        break;
    }
  }

  return parts.join('');
}

interface FunctionArgsResult {
  latex: string;
  argParts: string[];
  endIndex: number;
}

function extractFunctionArgs(tokens: Token[], openParenIndex: number): FunctionArgsResult {
  let depth = 1;
  let j = openParenIndex + 1;
  const argGroups: Token[][] = [[]];

  while (j < tokens.length && depth > 0) {
    const tok = tokens[j];
    if (tok.type === 'paren' && tok.value === '(') {
      depth++;
      argGroups[argGroups.length - 1].push(tok);
    } else if (tok.type === 'paren' && tok.value === ')') {
      depth--;
      if (depth === 0) {
        break;
      }
      argGroups[argGroups.length - 1].push(tok);
    } else if (tok.type === 'comma' && depth === 1) {
      argGroups.push([]);
    } else {
      argGroups[argGroups.length - 1].push(tok);
    }
    j++;
  }

  const argParts = argGroups.map((group) => (group.length > 0 ? tokensToLatex(group) : ''));
  const latex = argParts.join(', ');

  return { latex, argParts, endIndex: j };
}

interface OperandResult {
  latex: string;
  endIndex: number;
}

function getNextOperand(tokens: Token[], startIndex: number): OperandResult {
  if (startIndex >= tokens.length) {
    return { latex: '?', endIndex: startIndex - 1 };
  }

  const token = tokens[startIndex];

  if (token.type === 'number') {
    return { latex: token.value, endIndex: startIndex };
  }

  if (token.type === 'variable') {
    return { latex: escapeLatexVariable(token.value), endIndex: startIndex };
  }

  if (token.type === 'constant') {
    return { latex: LATEX_CONSTANT_MAP[token.value] ?? `\\text{${token.value}}`, endIndex: startIndex };
  }

  // Function call — capture function + its arguments
  if (token.type === 'function') {
    const subTokens = tokens.slice(startIndex);
    const endCount = findFunctionEnd(subTokens);
    const subLatex = tokensToLatex(subTokens.slice(0, endCount));
    const endIdx = startIndex + endCount - 1;
    return { latex: subLatex, endIndex: endIdx };
  }

  // Parenthesized group
  if (token.type === 'paren' && token.value === '(') {
    let depth = 1;
    const groupTokens: Token[] = [];
    let j = startIndex + 1;

    while (j < tokens.length && depth > 0) {
      if (tokens[j].type === 'paren' && tokens[j].value === '(') {
        depth++;
      } else if (tokens[j].type === 'paren' && tokens[j].value === ')') {
        depth--;
        if (depth === 0) {
          break;
        }
      }
      groupTokens.push(tokens[j]);
      j++;
    }

    const innerLatex = groupTokens.length > 0 ? tokensToLatex(groupTokens) : '';
    return { latex: innerLatex, endIndex: j };
  }

  return { latex: token.value, endIndex: startIndex };
}

function findFunctionEnd(tokens: Token[]): number {
  if (tokens.length < 2 || tokens[0].type !== 'function') {
    return 1;
  }
  if (tokens[1].type !== 'paren' || tokens[1].value !== '(') {
    return 1;
  }
  let depth = 1;
  let j = 2;
  while (j < tokens.length && depth > 0) {
    if (tokens[j].type === 'paren' && tokens[j].value === '(') {
      depth++;
    } else if (tokens[j].type === 'paren' && tokens[j].value === ')') {
      depth--;
    }
    j++;
  }
  return j;
}

/**
 * Fallback tokenizer-based LaTeX conversion.
 * Used when mathjs parsing fails (e.g. incomplete formulas during typing).
 */
function fallbackTokenizerToLatex(formula: string): string {
  const tokens = tokenize(formula);
  if (tokens.length === 0) {
    return '';
  }
  return tokensToLatex(tokens);
}
